import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import {
  ASO_TYPE_LABELS,
  deleteEmployeeDocumentApi,
  DOCUMENT_CATEGORY_LABELS,
  documentTypeName,
  DpDocumentType,
  DpEmployeeDocument,
  listDocumentTypesApi,
  listEmployeeDocumentsApi,
} from "../api/documentsApi";
import type { DpEmployeeDetail } from "../api/employeesApi";
import { formatDateOnly, orDash } from "../utils/format";
import DocumentExpiryBadge from "./DocumentExpiryBadge";
import DocumentFileButton from "./DocumentFileButton";
import DocumentFormModal from "./DocumentFormModal";

type Props = {
  token: string;
  employee: DpEmployeeDetail;
  /** Documento a destacar ao abrir (vindo de "Documentos a vencer"). */
  highlightedId?: string | null;
};

/** Aba "Documentos" da ficha: contrato, ASO, NRs e termos, com validade. */
export default function EmployeeDocumentsTab({ token, employee, highlightedId }: Props) {
  const [documents, setDocuments] = useState<DpEmployeeDocument[]>([]);
  const [types, setTypes] = useState<DpDocumentType[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<{ superseding?: DpEmployeeDocument; editing?: DpEmployeeDocument } | null>(
    null
  );
  const [toDelete, setToDelete] = useState<DpEmployeeDocument | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDocuments(await listEmployeeDocumentsApi(token, employee.id, showHistory));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os documentos.");
    } finally {
      setLoading(false);
    }
  }, [token, employee.id, showHistory]);

  useEffect(() => {
    load();
  }, [load]);

  // Rola até o documento destacado assim que a lista carrega (uma vez só por destaque).
  const highlightRef = useRef<HTMLTableRowElement | null>(null);
  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!highlightedId || loading || scrolledFor.current === highlightedId || !highlightRef.current) return;
    scrolledFor.current = highlightedId;
    highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightedId, loading, documents]);

  useEffect(() => {
    listDocumentTypesApi(token)
      .then(setTypes)
      .catch(() => setTypes([]));
  }, [token]);

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deleteEmployeeDocumentApi(token, toDelete.id);
      toast.success("Documento excluído.");
      setToDelete(null);
      load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir o documento.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="dpSection">
      <div className="dpHeader" style={{ marginBottom: 12 }}>
        <h2 className="dpSection__title" style={{ margin: 0 }}>
          Documentos
        </h2>
        <div className="dpHeader__actions">
          <label className="dpCheckbox">
            <input type="checkbox" checked={showHistory} onChange={(e) => setShowHistory(e.target.checked)} />
            Mostrar substituídos
          </label>
          <button className="dpBtn dpBtn--primary" onClick={() => setModal({})}>
            Novo documento
          </button>
        </div>
      </div>

      {types.length === 0 ? (
        <p className="dpNotice dpNotice--warning">
          Nenhum tipo de documento cadastrado. Crie em Cadastros → Tipos de documento (o botão "Criar
          padrões" monta os básicos).
        </p>
      ) : null}

      <div className="dpTableWrapper">
        <table className="dpTable">
          <thead>
            <tr>
              <th>Documento</th>
              <th>Emissão</th>
              <th>Validade</th>
              <th>Situação</th>
              <th style={{ width: 330 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="dpEmpty">
                  Carregando...
                </td>
              </tr>
            ) : documents.length === 0 ? (
              <tr>
                <td colSpan={5} className="dpEmpty">
                  Nenhum documento lançado.
                </td>
              </tr>
            ) : (
              documents.map((d) => {
                const superseded = Boolean(d.supersededById);
                return (
                  <tr
                    key={d.id}
                    ref={d.id === highlightedId ? highlightRef : undefined}
                    className={d.id === highlightedId ? "dpTableRow--highlight" : undefined}
                    style={superseded ? { opacity: 0.6 } : undefined}
                  >
                    <td>
                      <strong>{documentTypeName(d)}</strong>
                      {d.asoType ? ` · ${ASO_TYPE_LABELS[d.asoType]}` : ""}
                      {!d.documentType && !superseded ? (
                        <span className="dpBadge dpBadge--orange" style={{ marginLeft: 6 }}>
                          Sem tipo
                        </span>
                      ) : null}
                      <div style={{ fontSize: 12, color: "var(--eco-muted)" }}>
                        {d.documentType ? DOCUMENT_CATEGORY_LABELS[d.documentType.category] : "Defina em Editar"}
                        {d.title ? ` · ${d.title}` : ""}
                        {superseded ? " · substituído" : ""}
                      </div>
                      {d.notes ? <div style={{ fontSize: 12, color: "var(--eco-muted)" }}>{orDash(d.notes)}</div> : null}
                    </td>
                    <td>
                      {d.issuedAt || superseded ? (
                        formatDateOnly(d.issuedAt)
                      ) : (
                        <span className="dpBadge dpBadge--orange">Sem emissão</span>
                      )}
                    </td>
                    <td>{formatDateOnly(d.expiresAt)}</td>
                    <td>
                      {superseded ? (
                        <span className="dpBadge dpBadge--gray">Histórico</span>
                      ) : (
                        <DocumentExpiryBadge status={d.expiryStatus} daysToExpire={d.daysToExpire} />
                      )}
                    </td>
                    <td>
                      <div className="dpTable__actions">
                        {d.assetId ? (
                          <DocumentFileButton token={token} documentId={d.id} fileName={d.fileName} />
                        ) : (
                          <span className="dpFormHint">Sem arquivo</span>
                        )}
                        {!superseded ? (
                          <>
                            <button
                              className="dpBtn dpBtn--secondary"
                              style={{ height: 30, padding: "0 10px" }}
                              onClick={() => setModal({ editing: d })}
                            >
                              Editar
                            </button>
                            <button
                              className="dpBtn dpBtn--secondary"
                              style={{ height: 30, padding: "0 10px" }}
                              onClick={() => setModal({ superseding: d })}
                            >
                              Substituir
                            </button>
                            <button
                              className="dpBtn dpBtn--danger"
                              style={{ height: 30, padding: "0 10px" }}
                              onClick={() => setToDelete(d)}
                            >
                              Excluir
                            </button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {modal ? (
        <DocumentFormModal
          token={token}
          employeeId={employee.id}
          documentTypes={types}
          contracts={employee.contracts}
          superseding={modal.superseding}
          editing={modal.editing}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            load();
          }}
        />
      ) : null}

      <EcoConfirmDialog
        open={Boolean(toDelete)}
        title="Excluir o documento?"
        message={`"${toDelete ? documentTypeName(toDelete) : ""}${toDelete?.title ? ` · ${toDelete.title}` : ""}" sai da ficha. Use só para envio errado.`}
        confirmLabel="Excluir"
        tone="danger"
        busy={deleting}
        onConfirm={handleDelete}
        onCancel={() => setToDelete(null)}
      />
    </section>
  );
}
